"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { GRADE_VAR } from "@/styles/tokens";
import { PlaceMap } from "@/components/report/place-map";
import { ShareScore } from "@/components/share-score";
import type { CheckDelta } from "@/lib/delta-calculator";
import { createClient } from "@/lib/supabase-browser";

interface PublicPlaceData {
  place: { businessName: string; city: string; country: string; slug: string; googlePlaceId: string | null; address: string | null };
  score: {
    total: number;
    grade: string;
    components: Record<string, number>;
    actionCount: number;
    criticalActionCount: number;
    googleRating: number | null;
    googleReviewCount: number | null;
    websiteFound: boolean | null;
    hasSchemaMarkup: boolean | null;
    hasMenu: boolean | null;
    lastChecked: string;
  } | null;
  summary: string | null;
  totalChecks: number;
  whatsNew: {
    delta: CheckDelta;
    previousCheckedAt: string | null;
    currentCheckedAt: string;
    preservedSections: string[];
    mergedSections: string[];
  } | null;
}

const COMPONENT_LABELS: Record<string, string> = {
  web_readiness: "Web Readiness",
  mention_rate: "Mention Rate",
  position: "Competitive Position",
  sentiment: "AI Sentiment",
  engine_coverage: "Engine Coverage",
  knowledge_depth: "Knowledge Depth",
};

const COMPONENT_MAX: Record<string, number> = {
  web_readiness: 40,
  mention_rate: 15,
  position: 10,
  sentiment: 8,
  engine_coverage: 7,
  knowledge_depth: 20,
};

function ScoreBar({ label, value, max }: { label: string; value: number; max: number }) {
  const pct = Math.round((value / max) * 100);
  return (
    <div className="space-y-[var(--space-1)]">
      <div className="flex justify-between text-[length:var(--font-size-sm)]">
        <span className="text-[var(--color-text-muted)]">{label}</span>
        <span className="font-[number:var(--font-weight-medium)] text-[var(--color-text-primary)]">
          {value}/{max}
        </span>
      </div>
      <div className="h-2 rounded-[var(--radius-full)] bg-[var(--color-bg-muted)] overflow-hidden">
        <div
          className="h-full rounded-[var(--radius-full)] bg-[var(--color-accent)] transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function PublicPlaceView({ data }: { data: PublicPlaceData }) {
  const { place, score, summary, totalChecks, whatsNew } = data;
  const gradeColor = score ? GRADE_VAR[score.grade] || "" : "";

  // Auth-aware Claim CTA: logged-in users go straight to /new (with placeId
  // carried through so /new skips the Google lookup and can't land on a
  // different place). Logged-out users hit signup with ?next preserved.
  const [isAuthed, setIsAuthed] = useState<boolean | null>(null);
  useEffect(() => {
    const supabase = createClient();
    let active = true;
    supabase.auth.getUser().then(({ data: userData }) => {
      if (active) setIsAuthed(!!userData.user);
    }).catch(() => { if (active) setIsAuthed(false); });
    return () => { active = false; };
  }, []);

  const newCheckQs = new URLSearchParams({
    name: place.businessName,
    city: place.city,
    country: place.country,
  });
  if (place.googlePlaceId) newCheckQs.set("placeId", place.googlePlaceId);
  const newCheckPath = `/new?${newCheckQs.toString()}`;
  const claimHref = isAuthed
    ? newCheckPath
    : `/auth/signup?next=${encodeURIComponent(newCheckPath)}`;

  return (
    <div className="mx-auto max-w-[var(--container-xl)] space-y-[var(--space-6)] px-[var(--space-4)] py-[var(--space-6)] sm:py-[var(--space-8)] lg:px-[var(--space-8)]">
      {/* Header */}
      <div className="text-center space-y-[var(--space-2)]">
        <h1
          title={place.businessName}
          className="font-[family-name:var(--font-fraunces)] text-[length:var(--font-size-2xl)] sm:text-[length:var(--font-size-3xl)] font-[number:var(--font-weight-bold)] text-[var(--color-text-primary)] truncate"
        >
          {place.businessName}
        </h1>
        <p className="text-[length:var(--font-size-base)] sm:text-[length:var(--font-size-lg)] text-[var(--color-text-muted)]">
          {place.city}, {place.country}
        </p>
      </div>

      {score ? (
        <>
          {/* Score hero */}
          <div className="surface-warm rounded-[var(--radius-2xl)] sm:rounded-[var(--radius-3xl)] p-[var(--space-5)] sm:p-[var(--space-8)] text-center space-y-[var(--space-4)]">
            <p className="text-[length:var(--font-size-sm)] font-[number:var(--font-weight-medium)] uppercase tracking-wider text-[var(--color-text-muted)]">
              AI Visibility Score
            </p>
            <div className="flex items-baseline justify-center gap-[var(--space-2)] sm:gap-[var(--space-4)]">
              <span
                className="font-[family-name:var(--font-fraunces)] text-[length:var(--font-size-5xl)] sm:text-[length:var(--font-size-6xl)] font-[number:var(--font-weight-bold)] leading-none"
                style={{ color: gradeColor ? `var(${gradeColor})` : undefined }}
              >
                {score.total}
              </span>
              <span className="text-[length:var(--font-size-xl)] sm:text-[length:var(--font-size-2xl)] text-[var(--color-text-muted)]">/100</span>
            </div>
            <div
              className="inline-block rounded-[var(--radius-full)] px-[var(--space-4)] py-[var(--space-1)] text-[length:var(--font-size-lg)] font-[number:var(--font-weight-bold)]"
              style={{
                color: gradeColor ? `var(${gradeColor})` : undefined,
                backgroundColor: gradeColor ? `color-mix(in srgb, var(${gradeColor}) 12%, transparent)` : undefined,
              }}
            >
              Grade {score.grade}
            </div>
            {score.googleRating && (
              <p className="text-[length:var(--font-size-sm)] text-[var(--color-text-muted)]">
                Google Rating: {score.googleRating}/5
                {score.googleReviewCount ? ` (${score.googleReviewCount.toLocaleString()} reviews)` : ""}
              </p>
            )}
            <div className="pt-[var(--space-2)] flex justify-center">
              <ShareScore
                businessName={place.businessName}
                city={`${place.city}, ${place.country}`}
                score={score.total}
                grade={score.grade}
                shareUrl={`https://app.rekkd.io/place/${place.slug}`}
                variant="primary"
                rounded
              />
            </div>
          </div>

          {/* Summary */}
          {summary && (
            <div className="surface-warm rounded-[var(--radius-2xl)] p-[var(--space-5)] sm:p-[var(--space-6)] space-y-[var(--space-2)]">
              <h2 className="text-[length:var(--font-size-lg)] font-[number:var(--font-weight-semibold)] text-[var(--color-text-primary)]">
                AI Shadow Profile
              </h2>
              <p className="text-[length:var(--font-size-base)] text-[var(--color-text-secondary)] leading-relaxed">
                {summary}
              </p>
            </div>
          )}

          {/* Score breakdown */}
          <div className="surface-warm rounded-[var(--radius-2xl)] p-[var(--space-5)] sm:p-[var(--space-6)] space-y-[var(--space-4)]">
            <h2 className="text-[length:var(--font-size-lg)] font-[number:var(--font-weight-semibold)] text-[var(--color-text-primary)]">
              Score Breakdown
            </h2>
            <div className="space-y-[var(--space-3)]">
              {Object.entries(score.components).map(([key, value]) => (
                <ScoreBar
                  key={key}
                  label={COMPONENT_LABELS[key] || key}
                  value={value}
                  max={COMPONENT_MAX[key] || 10}
                />
              ))}
            </div>
          </div>

          {/* Quick facts */}
          <div className="surface-warm rounded-[var(--radius-2xl)] p-[var(--space-5)] sm:p-[var(--space-6)] space-y-[var(--space-3)]">
            <h2 className="text-[length:var(--font-size-lg)] font-[number:var(--font-weight-semibold)] text-[var(--color-text-primary)]">
              Quick Facts
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-[var(--space-2)] sm:gap-[var(--space-3)]">
              <Fact label="Website" value={score.websiteFound ? "Found" : "Not found"} good={!!score.websiteFound} />
              <Fact label="Schema.org" value={score.hasSchemaMarkup ? "Present" : "Missing"} good={!!score.hasSchemaMarkup} />
              <Fact label="Menu online" value={score.hasMenu ? "Yes" : "No"} good={!!score.hasMenu} />
              <Fact label="Issues found" value={String(score.actionCount)} good={score.actionCount === 0} />
            </div>
            {totalChecks > 1 && (
              <p className="text-[length:var(--font-size-xs)] text-[var(--color-text-muted)] pt-[var(--space-2)]">
                Analyzed {totalChecks} times
              </p>
            )}
          </div>
        </>
      ) : (
        <div className="surface-warm rounded-[var(--radius-3xl)] p-[var(--space-8)] text-center space-y-[var(--space-3)]">
          <p className="text-[length:var(--font-size-lg)] text-[var(--color-text-muted)]">
            This restaurant hasn&apos;t been analyzed yet.
          </p>
        </div>
      )}

      {/* Location Map */}
      <PlaceMap
        businessName={place.businessName}
        address={place.address}
        googlePlaceId={place.googlePlaceId}
      />

      {/* Claim CTA */}
      <div className="rounded-[var(--radius-2xl)] border border-[var(--color-primary)]/20 bg-[var(--color-primary-subtle)] p-[var(--space-5)] sm:p-[var(--space-6)] text-center space-y-[var(--space-4)]">
        <h2 className="text-[length:var(--font-size-lg)] sm:text-[length:var(--font-size-xl)] font-[number:var(--font-weight-bold)] text-[var(--color-text-primary)]">
          {score ? "Is this your restaurant?" : "Get your AI visibility report"}
        </h2>
        <p className="text-[length:var(--font-size-sm)] sm:text-[length:var(--font-size-base)] text-[var(--color-text-muted)]">
          {score
            ? "Claim this report to see your full shadow profile, competitor analysis, action plan, and track your score over time."
            : "Find out if AI recommends this restaurant. Get your score, shadow profile, and actionable fixes."}
        </p>
        <Link
          href={claimHref}
          className="inline-block w-full sm:w-auto rounded-[var(--radius-full)] bg-[var(--color-primary)] px-[var(--space-6)] py-[var(--space-3)] text-[length:var(--font-size-base)] font-[number:var(--font-weight-semibold)] text-[var(--color-primary-foreground)] hover:bg-[var(--color-primary-hover)] transition-colors"
        >
          {score ? "Claim Report — Free" : "Analyze Now — Free"}
        </Link>
      </div>

      {/* Embed badge */}
      {score && score.total >= 40 && (
        <EmbedBadge
          slug={place.slug}
          businessName={place.businessName}
          score={score.total}
          grade={score.grade}
        />
      )}

      {/* Last checked */}
      {score?.lastChecked && (
        <p className="text-center text-[length:var(--font-size-xs)] text-[var(--color-text-muted)]">
          Last analyzed {new Date(score.lastChecked).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          })}
        </p>
      )}
    </div>
  );
}

/** Grade colours mirror /api/badge/[slug] so preview + snippet match exactly. */
const BADGE_GRADE_COLORS: Record<string, string> = {
  A: "#22c55e",
  B: "#84cc16",
  C: "#eab308",
  D: "#f97316",
  F: "#ef4444",
};

/**
 * Render an inline SVG badge string the owner can paste directly on their
 * site. Self-contained: no external requests, renders everywhere. Tradeoff:
 * doesn't auto-update when the score changes — re-paste is required. A
 * future live-updating variant (via `<img src="/api/badge/<slug>">`) can be
 * offered as a second tab here.
 */
function renderBadgeSvg(
  businessName: string,
  score: number,
  grade: string,
): string {
  const color = BADGE_GRADE_COLORS[grade] || BADGE_GRADE_COLORS.C;
  const safeName = businessName.replace(/[<>&"']/g, (c) =>
    c === "<" ? "&lt;" :
    c === ">" ? "&gt;" :
    c === "&" ? "&amp;" :
    c === "\"" ? "&quot;" : "&#39;",
  );
  // Compact 240x64 card: left column is a solid grade tile, right column
  // shows the score + "AI Visibility · Rekkd" lockup.
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="240" height="64" viewBox="0 0 240 64" role="img" aria-label="${safeName} — AI Visibility Score by Rekkd">`,
    `<rect width="240" height="64" rx="8" fill="#0b0b0c"/>`,
    `<rect x="0" y="0" width="64" height="64" rx="8" fill="${color}"/>`,
    `<text x="32" y="42" text-anchor="middle" font-family="-apple-system,Segoe UI,Roboto,sans-serif" font-size="32" font-weight="700" fill="#ffffff">${grade}</text>`,
    `<text x="78" y="28" font-family="-apple-system,Segoe UI,Roboto,sans-serif" font-size="22" font-weight="700" fill="#ffffff">${score}<tspan font-size="12" fill="#9ca3af">/100</tspan></text>`,
    `<text x="78" y="44" font-family="-apple-system,Segoe UI,Roboto,sans-serif" font-size="10" font-weight="500" fill="#9ca3af" letter-spacing="0.6">AI VISIBILITY</text>`,
    `<text x="78" y="56" font-family="-apple-system,Segoe UI,Roboto,sans-serif" font-size="9" font-weight="600" fill="#ffffff">rekkd.io</text>`,
    `</svg>`,
  ].join("");
}

function EmbedBadge({
  slug,
  businessName,
  score,
  grade,
}: {
  slug: string;
  businessName: string;
  score: number;
  grade: string;
}) {
  const [copied, setCopied] = useState(false);
  const svg = renderBadgeSvg(businessName, score, grade);
  const snippet = `<a href="https://app.rekkd.io/place/${slug}" target="_blank" rel="noopener" style="text-decoration:none;display:inline-block;">${svg}</a>`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable — silently fall back to the pre element,
      // which users can still select and copy manually.
    }
  };

  return (
    <div className="surface-warm rounded-[var(--radius-2xl)] p-[var(--space-5)] sm:p-[var(--space-6)] space-y-[var(--space-4)]">
      <div className="space-y-[var(--space-1)]">
        <h2 className="text-[length:var(--font-size-lg)] font-[number:var(--font-weight-semibold)] text-[var(--color-text-primary)]">
          Show off your Rekkd grade
        </h2>
        <p className="text-[length:var(--font-size-sm)] text-[var(--color-text-muted)]">
          Paste this on your site — self-contained SVG, no external calls,
          and links back to this page so visitors can see your full
          AI-visibility report.
        </p>
      </div>

      {/* Live preview of the exact SVG the user is about to paste. */}
      <div className="flex justify-center rounded-[var(--radius-lg)] border border-dashed border-[var(--color-border-default)] bg-[var(--color-bg-card)] px-[var(--space-4)] py-[var(--space-5)]">
        <span
          aria-hidden="true"
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </div>

      <div className="space-y-[var(--space-2)]">
        <div className="flex items-center justify-between gap-[var(--space-2)]">
          <p className="text-[length:var(--font-size-xs)] font-[number:var(--font-weight-semibold)] uppercase tracking-[var(--letter-spacing-caps)] text-[var(--color-text-muted)]">
            HTML snippet
          </p>
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-[var(--space-1)] rounded-[var(--radius-full)] border border-[var(--color-border-default)] bg-[var(--color-bg-card)] px-[var(--space-3)] py-[var(--space-1)] text-[length:var(--font-size-xs)] font-[number:var(--font-weight-semibold)] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-muted)] transition-colors"
          >
            {copied ? "Copied!" : "Copy snippet"}
          </button>
        </div>
        <pre className="overflow-x-auto rounded-[var(--radius-lg)] bg-[var(--color-bg-muted)] p-[var(--space-3)] sm:p-[var(--space-4)] text-[length:var(--font-size-xs)] text-[var(--color-text-secondary)] leading-relaxed whitespace-pre">
          <code>{snippet}</code>
        </pre>
        <p className="text-[length:var(--font-size-xs)] text-[var(--color-text-muted)]">
          Score is baked in at paste time — re-run a check and re-paste to
          update.
        </p>
      </div>
    </div>
  );
}

function Fact({ label, value, good }: { label: string; value: string; good: boolean }) {
  return (
    <div className="flex items-center gap-[var(--space-2)] text-[length:var(--font-size-sm)]">
      <span className={good ? "text-[var(--color-success)]" : "text-[var(--color-text-muted)]"}>
        {good ? "\u2713" : "\u2717"}
      </span>
      <span className="text-[var(--color-text-muted)]">{label}:</span>
      <span className="text-[var(--color-text-primary)] font-[number:var(--font-weight-medium)]">{value}</span>
    </div>
  );
}
